import { createContext, useContext, useState, type ReactNode } from "react";
import type { variantGroupProductDTO, variantOptionsDTO } from "../dto/PdpPageProductDataDto";

export interface VariantContextType {
  variantGroups: variantGroupProductDTO[];
  onSelectVariant: (groupName: string, option: variantOptionsDTO) => void;
  variantLoading: boolean;
}

interface VariantContextProviderProps {
  children: ReactNode;
  variantGroupProductList?: variantGroupProductDTO[];
}

const VariantContext = createContext<VariantContextType | null>(null);

export const VariantContextProvider = ({ variantGroupProductList, children }: VariantContextProviderProps) => {
  const [variantGroups, setVariantGroups] = useState<variantGroupProductDTO[]>(variantGroupProductList ?? []);

  const [variantLoading, setVariantLoading] = useState(false);

  const onSelectVariant = (groupName: string, option: variantOptionsDTO) => {
    // Updating local selected state BEFORE redirect
    setVariantGroups((prev) =>
      prev.map((group) => ({
        ...group,
        options:
          group.name === groupName
            ? group.options.map((o) => ({
                ...o,
                selected: o.url === option.url, // updating selection only within the group
              }))
            : group.options,
      })),
    );

    setVariantLoading(true);
  };

  const value: VariantContextType = {
    variantGroups,
    onSelectVariant,
    variantLoading,
  };

  return <VariantContext.Provider value={value}>{children}</VariantContext.Provider>;
};

export const useVariantContext = (): VariantContextType => {
  const context = useContext(VariantContext);
  if (!context) {
    throw new Error("useVariantContext must be used within a VariantContextProvider");
  }
  return context;
};
